/**
 * Перепаковка записи MediaRecorder из WebM в Ogg.
 *
 * ── Зачем это вообще нужно ──────────────────────────────────────────────────
 *
 * Telegram принимает голосовые сообщения только как Ogg/Opus. Chromium — а
 * значит и оболочка Electron — записывать в Ogg не умеет:
 *
 *   MediaRecorder.isTypeSupported('audio/ogg;codecs=opus') === false
 *   MediaRecorder.isTypeSupported('audio/webm;codecs=opus') === true
 *
 * Проверено в этом самом рантайме, а не взято из документации.
 *
 * Существенно, что перекодировать НЕ нужно: внутри webm лежат ровно те же
 * пакеты Opus, которые ожидает Ogg. Различается только контейнер, поэтому
 * здесь распаковка одного и упаковка в другой — без потери качества и без
 * тяжёлых зависимостей вроде ffmpeg.wasm, который к тому же не прошёл бы CSP.
 *
 * ── Что здесь НЕ реализовано ────────────────────────────────────────────────
 *
 * Только то, что реально порождает MediaRecorder: один аудиотрек Opus,
 * SimpleBlock без лейсинга. Общего EBML-парсера тут нет и не должно быть —
 * он был бы кодом, который никто не исполняет, а значит и не проверяет.
 * Неожиданная структура приводит к явной ошибке, а не к тихо испорченному
 * файлу: голосовое, которое ушло собеседнику шумом, хуже несостоявшейся
 * отправки.
 */

/** Идентификаторы элементов EBML, которые нам нужны. */
const ID_SEGMENT = 0x18538067;
const ID_TRACKS = 0x1654ae6b;
const ID_TRACK_ENTRY = 0xae;
const ID_CODEC_PRIVATE = 0x63a2;
const ID_CLUSTER = 0x1f43b675;
const ID_SIMPLE_BLOCK = 0xa3;
const ID_BLOCK_GROUP = 0xa0;
const ID_BLOCK = 0xa1;

/** Элементы, внутрь которых нужно спускаться, а не пропускать целиком. */
const CONTAINERS = new Set([
  ID_SEGMENT,
  ID_TRACKS,
  ID_TRACK_ENTRY,
  ID_CLUSTER,
  ID_BLOCK_GROUP,
]);

interface Parsed {
  /** Содержимое CodecPrivate — это готовый заголовок OpusHead. */
  opusHead: Uint8Array | null;
  packets: Uint8Array[];
}

export class RemuxError extends Error {}

/**
 * Достать пакеты Opus из webm.
 *
 * Обход рекурсивный по вложенности, но плоский по памяти: наружу уходят
 * подмассивы исходного буфера, копий не делается.
 */
function parseWebm(data: Uint8Array): Parsed {
  const out: Parsed = { opusHead: null, packets: [] };

  const walk = (start: number, end: number): void => {
    let at = start;

    while (at < end) {
      const id = readElementId(data, at);
      if (id === null) return;
      at = id.next;

      const size = readVint(data, at);
      if (size === null) return;
      at = size.next;

      /*
       * Неизвестный размер (все биты единицы) допустим у Cluster и Segment в
       * потоковой записи — а MediaRecorder пишет именно потоком. Такой элемент
       * тянется до конца родителя.
       */
      const length = size.unknown ? end - at : Number(size.value);
      const stop = Math.min(at + length, end);

      if (CONTAINERS.has(id.value)) {
        walk(at, stop);
      } else if (id.value === ID_CODEC_PRIVATE) {
        out.opusHead = data.subarray(at, stop);
      } else if (id.value === ID_SIMPLE_BLOCK || id.value === ID_BLOCK) {
        const packet = readBlockFrame(data, at, stop);
        if (packet) out.packets.push(packet);
      }

      at = stop;
    }
  };

  walk(0, data.length);
  return out;
}

/**
 * Полезная нагрузка SimpleBlock: номер дорожки, метка времени, флаги, кадр.
 *
 * Лейсинг не поддерживается сознательно — MediaRecorder его не применяет, а
 * молча взять первый кадр из склейки значило бы обрезать звук.
 */
function readBlockFrame(
  data: Uint8Array,
  start: number,
  end: number,
): Uint8Array | null {
  const track = readVint(data, start);
  if (track === null) return null;

  // 2 байта метки времени + 1 байт флагов.
  const flagsAt = track.next + 2;
  if (flagsAt >= end) return null;

  const lacing = (data[flagsAt]! >> 1) & 0b11;
  if (lacing !== 0) {
    throw new RemuxError(
      "В записи применён лейсинг — такой webm мы не разбираем",
    );
  }

  return data.subarray(flagsAt + 1, end);
}

/** Идентификатор элемента читается ВМЕСТЕ с маркером длины. */
function readElementId(
  data: Uint8Array,
  at: number,
): { value: number; next: number } | null {
  const first = data[at];
  if (first === undefined) return null;

  const width = leadingZeroWidth(first);
  if (width === 0 || at + width > data.length) return null;

  let value = 0;
  for (let i = 0; i < width; i += 1) value = value * 256 + data[at + i]!;

  return { value, next: at + width };
}

/** Длина читается БЕЗ маркера: старший бит-маркер вычитается. */
function readVint(
  data: Uint8Array,
  at: number,
): { value: number; next: number; unknown: boolean } | null {
  const first = data[at];
  if (first === undefined) return null;

  const width = leadingZeroWidth(first);
  if (width === 0 || at + width > data.length) return null;

  let value = first & (0xff >> width);
  let allOnes = value === 0xff >> width;

  for (let i = 1; i < width; i += 1) {
    const byte = data[at + i]!;
    value = value * 256 + byte;
    if (byte !== 0xff) allOnes = false;
  }

  return { value, next: at + width, unknown: allOnes };
}

/** Ширина vint по позиции старшей единицы в первом байте. */
function leadingZeroWidth(byte: number): number {
  for (let i = 0; i < 8; i += 1) {
    if (byte & (0x80 >> i)) return i + 1;
  }
  return 0;
}

/**
 * Длительность пакета Opus в отсчётах при 48 кГц.
 *
 * Считается по TOC-байту, а не берётся как «наверняка 20 мс». Значение идёт в
 * granule position страниц Ogg, а по нему проигрыватель строит длительность:
 * ошибка здесь даёт голосовое с неверным таймингом, которое при этом
 * нормально звучит — то есть выглядит исправным.
 */
function packetSamples(packet: Uint8Array): number {
  const toc = packet[0];
  if (toc === undefined) return 0;

  const config = toc >> 3;
  const code = toc & 0b11;

  const sizeMs =
    config < 12
      ? [10, 20, 40, 60][config % 4]!
      : config < 16
        ? [10, 20][config % 2]!
        : [2.5, 5, 10, 20][config % 4]!;

  const frames =
    code === 0
      ? 1
      : code === 1 || code === 2
        ? 2
        : (packet[1] ?? 1) & 0b0011_1111;

  return Math.round(sizeMs * 48 * frames);
}

// ── Ogg ─────────────────────────────────────────────────────────────────────

/**
 * CRC контейнера Ogg.
 *
 * Полином 0x04c11db7 БЕЗ отражения битов, начальное значение 0, без финального
 * xor. Это не тот CRC-32, что в zip и png, и подстановка привычного даёт файл,
 * который отвергает любой проигрыватель.
 */
const CRC_TABLE = (() => {
  const table = new Uint32Array(256);
  for (let i = 0; i < 256; i += 1) {
    let value = i << 24;
    for (let bit = 0; bit < 8; bit += 1) {
      value =
        value & 0x8000_0000
          ? ((value << 1) ^ 0x04c1_1db7) >>> 0
          : (value << 1) >>> 0;
    }
    table[i] = value >>> 0;
  }
  return table;
})();

function oggCrc(bytes: Uint8Array): number {
  let crc = 0;
  for (const byte of bytes) {
    crc = ((crc << 8) ^ CRC_TABLE[((crc >>> 24) ^ byte) & 0xff]!) >>> 0;
  }
  return crc >>> 0;
}

interface PageInput {
  packets: Uint8Array[];
  granule: number;
  serial: number;
  sequence: number;
  first: boolean;
  last: boolean;
}

function buildPage(input: PageInput): Uint8Array {
  const lacing: number[] = [];
  for (const packet of input.packets) {
    let left = packet.length;
    while (left >= 255) {
      lacing.push(255);
      left -= 255;
    }
    lacing.push(left);
  }

  if (lacing.length > 255) {
    throw new RemuxError("Слишком много сегментов на странице Ogg");
  }

  const body = concat(input.packets);
  const page = new Uint8Array(
    new ArrayBuffer(27 + lacing.length + body.length),
  );
  const view = new DataView(page.buffer);

  page.set([0x4f, 0x67, 0x67, 0x53], 0); // "OggS"
  page[4] = 0; // версия
  page[5] = (input.first ? 0x02 : 0) | (input.last ? 0x04 : 0);

  /*
   * granule position — 64-битное поле. Число отсчётов записи заведомо меньше
   * 2^53, поэтому старшую половину пишем отдельно, а не через BigInt: так
   * ближе к тому, что тут реально происходит.
   */
  view.setUint32(6, input.granule >>> 0, true);
  view.setUint32(10, Math.floor(input.granule / 2 ** 32), true);

  view.setUint32(14, input.serial, true);
  view.setUint32(18, input.sequence, true);
  view.setUint32(22, 0, true); // CRC считается по странице с нулём в этом поле
  page[26] = lacing.length;
  page.set(lacing, 27);
  page.set(body, 27 + lacing.length);

  view.setUint32(22, oggCrc(page), true);
  return page;
}

/*
 * Возвращаемый тип указан явно: с TypeScript 5.7 Uint8Array параметризован
 * видом буфера, и без аннотации выводится ArrayBufferLike, который Blob не
 * принимает — SharedArrayBuffer туда класть нельзя.
 */
function concat(chunks: Uint8Array[]): Uint8Array<ArrayBuffer> {
  const total = chunks.reduce((sum, c) => sum + c.length, 0);
  const out = new Uint8Array(new ArrayBuffer(total));
  let at = 0;
  for (const chunk of chunks) {
    out.set(chunk, at);
    at += chunk.length;
  }
  return out;
}

/** Заголовок OpusTags. Обязателен по спецификации, содержимое произвольно. */
function opusTags(): Uint8Array {
  const vendor = new TextEncoder().encode("BUCOR WORKSPACE");
  const out = new Uint8Array(new ArrayBuffer(8 + 4 + vendor.length + 4));
  const view = new DataView(out.buffer);

  out.set(new TextEncoder().encode("OpusTags"), 0);
  view.setUint32(8, vendor.length, true);
  out.set(vendor, 12);
  view.setUint32(12 + vendor.length, 0, true); // ноль комментариев

  return out;
}

/**
 * Перепаковать запись MediaRecorder в Ogg/Opus.
 *
 * @throws RemuxError если структура записи не та, которую мы умеем разбирать.
 */
/** Готовое голосовое: файл и его длительность в секундах. */
export interface RemuxResult {
  blob: Blob;
  /**
   * Длительность в СЕКУНДАХ, посчитанная по пакетам Opus.
   *
   * Отдаётся наружу не для показа, а для отправки: TDLib длительность
   * голосового НЕ определяет сам — он берёт то, что ему передали, и ноль
   * уходит в Telegram как есть. Голосовое «0:00» собеседник не проигрывает.
   */
  seconds: number;
}

export async function webmToOggOpus(blob: Blob): Promise<RemuxResult> {
  const data = new Uint8Array(await blob.arrayBuffer());
  const { opusHead, packets } = parseWebm(data);

  if (!opusHead || opusHead.length < 19) {
    throw new RemuxError("В записи не нашёлся заголовок OpusHead");
  }
  if (packets.length === 0) {
    throw new RemuxError("В записи не нашлось ни одного пакета Opus");
  }

  // Серийный номер потока произволен, но должен быть один на файл.
  const serial = (Math.random() * 0xffff_ffff) >>> 0;
  const pages: Uint8Array[] = [];
  let sequence = 0;

  pages.push(
    buildPage({
      packets: [opusHead],
      granule: 0,
      serial,
      sequence: sequence++,
      first: true,
      last: false,
    }),
  );
  pages.push(
    buildPage({
      packets: [opusTags()],
      granule: 0,
      serial,
      sequence: sequence++,
      first: false,
      last: false,
    }),
  );

  /*
   * Пакеты пакуются по 50 на страницу.
   *
   * Ограничений два: не больше 255 сегментов на страницу и разумный размер
   * самой страницы. При кадре в 20 мс полсотни пакетов дают ровно секунду
   * звука на страницу — удобно и заведомо укладывается в оба предела, пока
   * пакет короче пяти сегментов.
   */
  let granule = 0;
  for (let i = 0; i < packets.length; i += 50) {
    const chunk = packets.slice(i, i + 50);
    for (const packet of chunk) granule += packetSamples(packet);

    pages.push(
      buildPage({
        packets: chunk,
        granule,
        serial,
        sequence: sequence++,
        first: false,
        last: i + 50 >= packets.length,
      }),
    );
  }

  return {
    blob: new Blob([concat(pages)], { type: "audio/ogg" }),
    seconds: estimateDuration(packets),
  };
}

/** Длительность записи в секундах — по тем же пакетам, без декодирования. */
export function estimateDuration(packets: Uint8Array[]): number {
  return packets.reduce((sum, p) => sum + packetSamples(p), 0) / 48_000;
}
